<script lang="ts">
import { defineComponent } from 'vue'
import MailDataService from '@/services/MailDataService'
import type Mail from '@/types/Mail'
import type ResponseData from '@/types/ResponseData'
import UserDataService from '@/services/UserDataService'
import type ObjectConfig from '@/types/ObjectConfig'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import moment from 'moment'
import imageCompression from 'browser-image-compression'
import heic2any from 'heic2any'
import VueEasyLightbox from 'vue-easy-lightbox'
import 'vue-easy-lightbox/dist/external-css/vue-easy-lightbox.css'

export default defineComponent({
  name: 'mail-detail',
  components: { VueEasyLightbox },
  data() {
    return {
      currentMail: {} as Mail,
      currentCompany: {} as Company,
      message: '',
      userName: '',
      listOfCompanies: [] as Company[],
      mailStatuses: [] as ObjectConfig[],
      mailTypes: [] as ObjectConfig[],
      imageUrl: '',
      isOpened: false,
      showStatusModal: false,
      uploadImageData: { name: '', image: '' },
      formatedDate: '',
      envelopePhotoFile: null as File | null,
      envelopePhotoPreviewUrl: '' as string,
      envelopeLightboxImages: [] as string[],
      showEnvelopeLightbox: false,
    }
  },
  methods: {
    async getAllCompanies() {
      // Fetch all companies from all pages
      let allCompanies: Company[] = [];
      let page = 1;
      let lastPage = 1;
      do {
        try {
          const response: ResponseData = await CompanyDataService.getAllCompanies(page);
          const data = response.data.data;
          if (Array.isArray(data.data)) {
            allCompanies = allCompanies.concat(data.data);
          }
          lastPage = data.last_page || 1;
          page++;
        } catch (e: any) {
          toast.error('Chyba: ' + e.message);
          break;
        }
      } while (page <= lastPage);
      this.listOfCompanies = allCompanies;
    },
    getCompany(id: any) {
      CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
          this.currentCompany = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getMail(id: any) {
      MailDataService.getMailById(id)
        .then((response: ResponseData) => {
          this.currentMail = response.data.data
          // Handle both string and date formats for distribution_date
          this.formatedDate = moment(this.currentMail.distribution_date).format("YYYY-MM-DD");
          this.getCompany(this.currentMail.company_id)
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    updateMail() {
      // Create a copy of currentMail with distribution_date as string for API call
      const updatePayload: Partial<Mail & { distribution_date: string }> = {
        ...this.currentMail,
        distribution_date: this.formatedDate
      };

      MailDataService.update(this.currentMail.id, updatePayload)
        .then((response: ResponseData) => {
          this.message = 'The Mail was updated successfully!'
          this.$router.push({ name: 'Zoznam zásielok' })
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    deleteMail() {
      MailDataService.delete(this.currentMail.id)
        .then((response: ResponseData) => {
          this.$router.push({ name: 'Zoznam zásielok' })
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getMailStatuses() {
      MailDataService.getMailStatuses()
        .then((response: ResponseData) => {
          this.mailStatuses = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getMailTypes() {
      MailDataService.getMailTypes()
        .then((response: ResponseData) => {
          this.mailTypes = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    updateImage(evt: any) {
      this.currentMail.scan = evt.target.files[0].name
      this.uploadImageData.name = evt.target.files[0].name
      this.uploadImageData.image = evt.target.files[0]
      MailDataService.uploadImage(this.currentMail.id, this.uploadImageData)
        .then((response: ResponseData) => {
          this.imageUrl = response.data.image_url
          this.$emit('imageUrl', response.data.secure_url)
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    forwardMail() {
      this.currentMail.status = 4
      this.currentMail.forward_requested = false
      MailDataService.update(this.currentMail.id, this.currentMail)
        .then(() => {
          this.message = 'The Mail was updated successfully!'
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    shredMail() {
      this.currentMail.status = 2
      this.currentMail.shred_requested = false
      MailDataService.update(this.currentMail.id, this.currentMail)
        .then(() => {
          this.message = 'The Mail was updated successfully!'
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    onChange(event: any) {
      this.currentMail.company_id = event.target.value
    },
    async handleEnvelopePhoto(evt: any) {
      const original: File = evt.target.files && evt.target.files[0]
      if (!original) return

      try {
        // iPhones default to HEIC since iOS 11. Decode to JPEG in the browser
        // so the file stored on the BE is always a broadly-supported format.
        const isHeic = original.type === 'image/heic' || /\.(heic|heif)$/i.test(original.name)
        const decoded: Blob = isHeic
          ? (await heic2any({ blob: original, toType: 'image/jpeg', quality: 0.92 })) as Blob
          : original

        // Wrap the decoded blob as a File with a .jpg name so the BE validator
        // (mimes:jpg,jpeg,png) accepts it.
        const decodedFile: File = decoded instanceof File
          ? decoded
          : new File([decoded], this.replaceExtension(original.name, 'jpg'), { type: 'image/jpeg' })

        // Compress on a web worker to keep the UI responsive and strip EXIF
        // (including GPS) before it leaves the device.
        const compressed: File = await imageCompression(decodedFile, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1600,
          useWebWorker: true,
          fileType: 'image/jpeg',
          initialQuality: 0.85,
        })

        this.envelopePhotoFile = compressed
        // Revoke the previous preview URL before overwriting to avoid a leak
        // when the user re-picks a file before the old preview is closed.
        if (this.envelopePhotoPreviewUrl) {
          URL.revokeObjectURL(this.envelopePhotoPreviewUrl)
        }
        this.envelopePhotoPreviewUrl = URL.createObjectURL(compressed)
        this.showEnvelopeLightbox = false
      } catch (e: any) {
        toast.error('Spracovanie fotky obálky zlyhalo: ' + (e?.message || e))
        this.envelopePhotoFile = null
        this.envelopePhotoPreviewUrl = ''
      }
    },
    uploadEnvelopePhotoNow() {
      if (!this.envelopePhotoFile || !this.currentMail.id) return
      MailDataService.uploadEnvelopePhoto(this.currentMail.id, this.envelopePhotoFile)
        .then((response: ResponseData) => {
          this.currentMail.envelope_photo = response.data.envelope_photo
          this.currentMail.envelope_photo_url = response.data.envelope_photo_url
          this.envelopePhotoFile = null
          if (this.envelopePhotoPreviewUrl) {
            URL.revokeObjectURL(this.envelopePhotoPreviewUrl)
            this.envelopePhotoPreviewUrl = ''
          }
          const input = document.getElementById('envelope_photo') as HTMLInputElement | null
          if (input) input.value = ''
          toast.success('Fotka obálky bola aktualizovaná')
        })
        .catch((e: Error) => {
          toast.error('Upload fotky obálky zlyhal: ' + e.message)
        })
    },
    deleteEnvelopePhotoNow() {
      if (!this.currentMail.id) return
      if (!confirm('Naozaj zmazať fotku obálky tejto zásielky?')) return
      MailDataService.deleteEnvelopePhoto(this.currentMail.id)
        .then(() => {
          this.currentMail.envelope_photo = null
          this.currentMail.envelope_photo_url = null
          toast.success('Fotka obálky bola zmazaná')
        })
        .catch((e: Error) => {
          toast.error('Mazanie fotky obálky zlyhalo: ' + e.message)
        })
    },
    openEnvelopeLightbox() {
      const src = this.envelopePhotoPreviewUrl || this.currentMail.envelope_photo_url
      if (!src) return
      this.envelopeLightboxImages = [src]
      this.showEnvelopeLightbox = true
    },
    replaceExtension(name: string, ext: string) {
      return name.replace(/\.[^./\\]+$/, '') + '.' + ext
    },
    goBack() {
      this.$router.go(-1)
    },
  },
  mounted() {
    this.message = ''
    this.getAllCompanies()
    this.getMail(this.$route.params.id)
    this.getMailStatuses()
    this.getMailTypes()
  },
  beforeUnmount() {
    if (this.envelopePhotoPreviewUrl) {
      URL.revokeObjectURL(this.envelopePhotoPreviewUrl)
    }
  },
})
</script>
<template>
  <main>
    <div class="flex flex-row justify-between max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6">
      <div class="basis-1/4">
        <button @click="goBack" type="button"
          class="flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
            stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Späť
        </button>
      </div>
      <div class="basis-1/4">
        <h1 class="text-2xl font-semibold text-gray-900">Detail zásielky - {{ currentMail.id }}</h1>
      </div>
      <div class="basis-1/4">
        <button @click="isOpened = true" type="button"
          class="focus:outline-none text-white bg-red-700 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900">
          Zmazať
        </button>
      </div>
    </div>
    <div v-show="isOpened" id="alert-dialog"
      class="p-4 mb-4 border border-red-300 rounded-lg bg-red-50 dark:bg-red-200 max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6"
      role="alert">
      <div class="flex items-center">
        <svg aria-hidden="true" class="w-5 h-5 mr-2 text-red-900 dark:text-red-800" fill="currentColor"
          viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            clip-rule="evenodd"></path>
        </svg>
        <span class="sr-only">Info</span>
        <h3 class="text-lg font-medium text-red-900 dark:text-red-800">Zmazanie záznamu z databázy</h3>
      </div>
      <div class="mt-2 mb-4 text-sm text-red-900 dark:text-red-800">
        Naozaj chcete zmazať tento záznam z databázy? Je to nenávratné :(
      </div>
      <div class="flex">
        <button type="button"
          class="text-white bg-red-900 hover:bg-red-800 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-xs px-3 py-1.5 mr-2 text-center inline-flex items-center dark:bg-red-800 dark:hover:bg-red-900"
          @click="deleteMail()">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
            stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>

          Zmazať
        </button>
        <button type="button"
          class="text-red-900 bg-transparent border border-red-900 hover:bg-red-900 hover:text-white focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-xs px-3 py-1.5 text-center dark:border-red-800 dark:text-red-800 dark:hover:text-white"
          data-dismiss-target="#alert-additional-content-2" aria-label="Close" @click="isOpened = false">
          Zrušiť
        </button>
      </div>
    </div>
    <!-- Status Modal -->
    <div v-if="showStatusModal" class="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog"
      aria-modal="true">
      <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"
          @click="showStatusModal = false"></div>

        <!-- Modal panel -->
        <div
          class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 sticky top-0 bg-white py-2" id="modal-title">
                  Stavy zásielok
                </h3>
                <div class="mt-2 max-h-96 overflow-y-auto">
                  <div class="overflow-hidden border rounded-lg">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50 sticky top-0">
                        <tr>
                          <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                            ID
                          </th>
                          <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                            Názov
                          </th>
                          <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                            Popis
                          </th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-gray-200">
                        <tr v-for="status in mailStatuses" :key="status.id">
                          <td class="px-6 py-4 text-sm font-medium text-gray-800">
                            {{ status.id }}
                          </td>
                          <td class="px-6 py-4 text-sm text-gray-800">{{ status.name }}</td>
                          <td class="px-6 py-4 text-sm text-gray-800">
                            {{ status.description }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button type="button"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              @click="showStatusModal = false">
              Zatvoriť
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="px-4 py-6 sm:px-0">
        <div class="mt-10 sm:mt-0">
          <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1">
              <div class="px-4 sm:px-0">
                <div class="pb-6">
                  <h3 class="text-lg font-medium leading-6 text-gray-900">Informácie o zásielke</h3>
                </div>
                <p class="text-sm text-gray-500">Vyplňte všetky potrebné informácie o zásielke.</p>
              </div>
            </div>
            <div class="mt-5 md:mt-0 md:col-span-2">
              <div class="pb-6">
                <h3 class="text-lg font-medium leading-6 text-gray-900">Informácie o zásielke</h3>
              </div>
              <form @submit.prevent="updateMail">
                <div class="shadow overflow-hidden sm:rounded-md">
                  <div class="px-4 py-5 bg-white sm:p-6">
                    <div class="grid grid-cols-6 gap-6">
                      <div class="col-span-6 sm:col-span-3">
                        <label for="sender" class="block text-sm font-medium text-gray-700">Odosielateľ</label>
                        <FormKit type="text" v-model="currentMail.sender" name="sender" id="sender"
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <label for="distribution_date" class="block text-sm font-medium text-gray-700">Dátum
                          prijatia</label>
                        <FormKit type="date" v-model="formatedDate" :value="formatedDate" name="distribution_date"
                          id="distribution_date"
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <label for="company_id">Adresát</label>
                        <select id="company_id" v-model="currentMail.company_id" @change="onChange($event)"
                          class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm">
                          <option v-if="!listOfCompanies.length">Žiadny klienti sa nenašli</option>
                          <option v-for="user in listOfCompanies" :key="user.id"
                            v-bind:selected="currentMail.company_id == currentCompany.id" :value="user.id">
                            {{ user.name }}, {{ user.ico }}
                          </option>
                        </select>
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <label for="type" class="block text-sm font-medium text-gray-700">Typ zásielky</label>
                        <select v-model="currentMail.type" name="type" id="type"
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                          <option value="">Vyberte typ zásielky</option>
                          <option v-for="mailType in mailTypes" :key="mailType.id" :value="mailType.id">{{ mailType.name
                          }}</option>
                        </select>
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <div class="flex items-center">
                          <label for="status" class="block text-sm font-medium text-gray-700 mr-2">Stav zásielky</label>
                          <button type="button" @click="showStatusModal = true"
                            class="text-gray-400 hover:text-gray-500 focus:outline-none">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                              fill="currentColor">
                              <path fill-rule="evenodd"
                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <select v-model="currentMail.status" name="status" id="status"
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                          <option value="">Vyberte stav zásielky</option>
                          <option v-for="status in mailStatuses" :key="status.id" :value="status.id">{{ status.name }}
                          </option>
                        </select>
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <label for="weight" class="block text-sm font-medium text-gray-700">Gramáž</label>
                        <FormKit type="text" number v-model="currentMail.weight" name="weight" id="weight"
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <label for="scan" class="block text-sm font-medium text-gray-700">Súbor scan: {{
                          currentMail.scan
                        }}</label>
                        <FormKit
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          id="scan" accept="image/*" v-on:change="updateImage($event)" name="scan" type="file" />
                      </div>

                      <div class="col-span-6 sm:col-span-3">
                        <label for="envelope_photo" class="block text-sm font-medium text-gray-700">Fotka obálky</label>
                        <input
                          id="envelope_photo"
                          name="envelope_photo"
                          type="file"
                          accept="image/*,.heic,.heif"
                          class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          @change="handleEnvelopePhoto($event)" />
                        <div class="mt-2 flex items-center gap-3">
                          <img
                            v-if="envelopePhotoPreviewUrl || currentMail.envelope_photo_url"
                            :src="envelopePhotoPreviewUrl || currentMail.envelope_photo_url"
                            alt="Náhľad obálky"
                            class="w-32 h-32 object-cover rounded-md border border-gray-300 cursor-pointer"
                            @click="openEnvelopeLightbox" />
                          <div class="flex flex-col gap-2">
                            <button
                              v-if="envelopePhotoPreviewUrl"
                              type="button"
                              class="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md"
                              :disabled="!envelopePhotoFile"
                              @click="uploadEnvelopePhotoNow">
                              Uložiť novú fotku
                            </button>
                            <button
                              v-if="currentMail.envelope_photo && !envelopePhotoPreviewUrl"
                              type="button"
                              class="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                              @click="deleteEnvelopePhotoNow">
                              Zmazať fotku
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="col-span-6">
                        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
                          <div class="rounded-lg border border-gray-200 p-4">
                            <h4 class="text-sm font-semibold text-gray-900 mb-3">Scan</h4>
                            <div class="space-y-3">
                              <label class="flex items-center gap-3 cursor-pointer">
                                <input id="scan_requested" v-model="currentMail.scan_requested"
                                  :checked="currentMail.scan_requested" type="checkbox"
                                  class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 rounded-md text-teal-500 border-gray-300" />
                                <span class="text-sm text-gray-700">Požiadané o scan?</span>
                              </label>
                              <label class="flex items-center gap-3 cursor-pointer">
                                <input id="scan_free" v-model="currentMail.scan_free"
                                  :checked="currentMail.scan_free" type="checkbox"
                                  class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 rounded-md text-teal-500 border-gray-300" />
                                <span class="text-sm text-gray-700">Zadarmo - scan?</span>
                              </label>
                            </div>
                          </div>

                          <div class="rounded-lg border border-gray-200 p-4">
                            <h4 class="text-sm font-semibold text-gray-900 mb-3">Preposlanie</h4>
                            <div class="space-y-3">
                              <label class="flex items-center gap-3 cursor-pointer">
                                <input id="forward_requested" v-model="currentMail.forward_requested"
                                  :checked="currentMail.forward_requested" type="checkbox"
                                  class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 rounded-md text-teal-500 border-gray-300" />
                                <span class="text-sm text-gray-700">Požiadané o preposlanie?</span>
                              </label>
                              <label class="flex items-center gap-3 cursor-pointer">
                                <input id="preposlanie_free" v-model="currentMail.preposlanie_free"
                                  :checked="currentMail.preposlanie_free" type="checkbox"
                                  class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 rounded-md text-teal-500 border-gray-300" />
                                <span class="text-sm text-gray-700">Zadarmo - preposlanie?</span>
                              </label>
                            </div>
                            <div v-if="currentMail.forward_requested" class="mt-4 space-y-3 border-t border-gray-100 pt-3">
                              <label class="block text-sm text-gray-700"><b>Meno adresáta:</b> {{
                                currentMail.receiver }}</label>
                              <label class="block text-sm text-gray-700"><b>Adresa:</b> {{
                                currentMail.forward_address }}</label>
                              <button type="button" v-if="currentMail.status != 4"
                                class="w-full py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                v-on:click="forwardMail()">
                                Preposlať
                              </button>
                            </div>
                            <label for="tracking_number" class="block text-sm font-medium text-gray-700 mt-4">Sledovacie
                              číslo</label>
                            <FormKit type="text" v-model="currentMail.tracking_number" name="tracking_number"
                              id="tracking_number"
                              class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                            <div v-if="currentMail.status == 4" class="mt-3 space-y-1">
                              <label class="block text-sm text-gray-700"><b>Adresa:</b> {{
                                currentMail.forward_address }}</label>
                              <p class="text-sm font-medium text-gray-700">Preposlané</p>
                            </div>
                          </div>

                          <div class="rounded-lg border border-gray-200 p-4">
                            <h4 class="text-sm font-semibold text-gray-900 mb-3">Skartovanie</h4>
                            <label class="flex items-center gap-3 cursor-pointer">
                              <input id="shred_requested" v-model="currentMail.shred_requested"
                                :checked="currentMail.shred_requested" type="checkbox"
                                class="w-4 h-4 focus:ring-teal-500 focus:border-teal-500 rounded-md text-teal-500 border-gray-300" />
                              <span class="text-sm text-gray-700">Požiadané o skartovanie?</span>
                            </label>
                            <button type="button" v-if="currentMail.shred_requested" v-on:click="shredMail()"
                              class="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">
                              Skartovať
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                    <button type="submit"
                      class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
                      Uložiť
                    </button>
                  </div>
                </div>
              </form>
            </div>
            <!---->
          </div>
        </div>
      </div>
    </div>
    <vue-easy-lightbox
      v-if="envelopeLightboxImages.length > 0"
      :visible="showEnvelopeLightbox"
      :imgs="envelopeLightboxImages"
      :index="0"
      @hide="showEnvelopeLightbox = false" />
  </main>
</template>
